import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmtDate, fmt } from '../utils';
import { PageWrap, PageHeader, Card, CardHeader, Badge, Skeleton, useIsMobile } from '../components/UI';
import { AUDIT_STATUS_CONFIG, CA_AUDIT_STATUSES } from '../auditStatus';
import '../styles/AuditReport.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const CUSTOMER_STATUSES = ['pending_ca'];

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (saved) {
      const parsed   = JSON.parse(saved);
      const account  = parsed?.account;
      const email    = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      const name     = account?.name || account?.idTokenClaims?.name || account?.username?.split('@')[0] || '';
      const viewMode = localStorage.getItem('auditpros_view_mode') || 'ca';
      const headers  = { 'Content-Type': 'application/json' };
      if (email) headers['x-user-email'] = email;
      if (name)  headers['x-user-name']  = name;
      headers['x-view-mode'] = viewMode;
      return headers;
    }
  } catch { /* ignore */ }
  return { 'Content-Type': 'application/json' };
}

function fmtIST(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function authorLabel(comment) {
  const name = comment.author_name || 'Unknown';
  if (comment.author_role === 'admin')  return `${name} (Admin)`;
  if (comment.author_role === 'ca')     return `${name} (CA)`;
  if (comment.author_role === 'system') return 'System';
  return name;
}

function StatusBadge({ status }) {
  const cfg = AUDIT_STATUS_CONFIG[status] || AUDIT_STATUS_CONFIG.synced;
  if (!cfg) return null;
  return (
    <span
      className="ar-status-badge"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}22` }}
    >
      {cfg.label}
    </span>
  );
}


// ── Comments Section ──────────────────────────────────────────────────────────
function CommentsSection({ sourceId, syncRunId, userRole, selectedPdfIds, setSelectedPdfIds }) {
  const [comments,  setComments]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [text,      setText]      = useState('');
  const [posting,   setPosting]   = useState(false);
  const [error,     setError]     = useState('');
  const textareaRef = useRef(null);
  const isCA = userRole?.isCA || userRole?.isAdmin || false;

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(
        `${BASE}/audit-comments?syncRunId=${syncRunId}&sourceId=${sourceId}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (res.ok) {
        const list = Array.isArray(data) ? data : data.comments || [];
        setComments(list);
        const latestCA = list.find(c =>
          (c.author_role === 'ca' || c.author_role === 'admin') && !c.is_status_change
        );
        if (latestCA) {
          setSelectedPdfIds(new Set([latestCA.id]));
        }
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [sourceId, syncRunId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const handlePost = async () => {
    if (text.trim().length === 0) return;
    if (text.trim().length < 20) { setError('Comment must be at least 20 characters.'); return; }
    setPosting(true); setError('');
    try {
      const res  = await fetch(`${BASE}/audit-comments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sourceId, syncRunId, comment: text.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to post comment');
      setComments(prev => [data, ...prev]);
      setText('');
      textareaRef.current?.focus();
    } catch (err) { setError(err.message); }
    finally { setPosting(false); }
  };

  const handleTogglePdf = (commentId) => {
    setSelectedPdfIds(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  };

  const pdfCount = selectedPdfIds.size;

  return (
    <Card>
      <CardHeader
        title="Auditor Comments & Responses"
        right={isCA && pdfCount > 0 ? (
          <span className="ar-pdf-count-pill">
            {pdfCount} comment{pdfCount !== 1 ? 's' : ''} in PDF
          </span>
        ) : null}
      />

      {/* Compose box */}
      <div className="ar-compose-wrap">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={isCA ? 'Add an auditor comment or observation…' : 'Respond to auditor comments…'}
          rows={3}
          className="ar-textarea"
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handlePost(); }}
        />
        <div className="ar-compose-footer">
          <span className={text.trim().length > 0 && text.trim().length < 20 ? 'ar-compose-hint ar-compose-hint--warn' : 'ar-compose-hint'}>
            {text.trim().length > 0 && text.trim().length < 20
              ? `${text.trim().length}/20 min chars`
              : '⌘↵ to post'}
          </span>
          <div className="ar-compose-actions">
            {error && <span className="ar-post-error">{error}</span>}
            <button
              onClick={handlePost}
              disabled={posting || text.trim().length < 20}
              className="ar-btn-post"
              style={{ background: isCA ? 'var(--accent)' : '#0891B2' }}
            >
              {posting ? 'Posting…' : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>

      {/* CA hint */}
      {isCA && (
        <div className="ar-ca-hint">
          <span className="ar-ca-hint-icon">🖨</span>
          Check the <strong style={{ color: 'var(--text2)' }}>PDF</strong> checkbox on any comment to include it in the printed report.
          By default the latest CA comment is included.
        </div>
      )}

      {/* Comment list — newest first */}
      <div className="ar-comment-list">
        {loading ? (
          <div className="ar-comment-loading">Loading…</div>
        ) : comments.length === 0 ? (
          <div className="ar-comment-empty">
            <div className="ar-comment-empty-icon">💬</div>
            No comments yet. Be the first to add one.
          </div>
        ) : (
          comments.map((c, i) => {
            const isSystem     = c.author_role === 'system';
            const isStatusNote = c.is_status_change;
            const isAuthorCA   = c.author_role === 'ca' || c.author_role === 'admin';
            const inPdf        = selectedPdfIds.has(c.id);
            const isToggling   = false;

            // Status-change / system entries — centred pill
            if (isSystem || isStatusNote) {
              return (
                <div
                  key={c.id || i}
                  className="ar-system-entry"
                  style={{ borderBottom: i < comments.length - 1 ? '1px solid var(--border)' : 'none' }}
                >
                  <div className="ar-system-line" />
                  <div style={{ textAlign: 'center' }}>
                    <span className="ar-system-pill">
                      {isStatusNote && c.new_status && <StatusBadge status={c.new_status} />}
                      {' '}{authorLabel(c)} · {fmtIST(c.created_at)}
                    </span>
                    {c.comment && (
                      <div className="ar-system-comment">{c.comment}</div>
                    )}
                  </div>
                  <div className="ar-system-line" />
                </div>
              );
            }

            // Regular comment bubble
            return (
              <div key={c.id || i} style={{
                padding: '14px 18px',
                borderBottom: i < comments.length - 1 ? '1px solid var(--border)' : 'none',
                background: inPdf
                  ? 'rgba(79,142,247,0.04)'
                  : i % 2 === 0 ? 'transparent' : 'var(--bg2)',
                borderLeft: inPdf ? '3px solid var(--accent)' : '3px solid transparent',
                transition: 'all 0.15s',
              }}>
                <div className="ar-comment-header">
                  <div
                    className="ar-comment-avatar"
                    style={{ background: isAuthorCA ? 'var(--accent)' : '#0891B2' }}
                  >
                    {(c.author_name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <span className="ar-comment-author">{authorLabel(c)}</span>
                    <span className="ar-comment-time">{fmtIST(c.created_at)}</span>
                  </div>
                  {isAuthorCA && <span className="ar-comment-ca-badge">CA</span>}

                  {/* PDF toggle — CA/Admin only */}
                  {isCA && (
                    <label
                      className="ar-comment-pdf-toggle"
                      style={{
                        color: inPdf ? 'var(--accent)' : 'var(--text3)',
                        fontWeight: inPdf ? 600 : 400,
                        opacity: isToggling ? 0.5 : 1,
                        cursor: isToggling ? 'wait' : 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={inPdf}
                        disabled={isToggling}
                        onChange={() => handleTogglePdf(c.id)}
                        className="ar-comment-checkbox"
                      />
                      PDF
                    </label>
                  )}
                </div>
                <div className="ar-comment-body">{c.comment}</div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}

// ── Issue type sets for pie charts ────────────────────────────────────────────
const CRITICAL_TYPES = new Set(['STATUTORY_UNPAID', 'EXCESS_PAYMENT', 'UNBALANCED_VOUCHER', 'DUPLICATE_VOUCHER_NO', 'MISSING_GST', 'INCORRECT_GST_RATE', 'INVALID_GSTIN', 'TDS_NOT_DEDUCTED', 'GST_CALCULATION_ERROR', 'MISSING_GSTIN_PURCHASE', 'TRIAL_BALANCE_DIFFERENCE']);
const WARNING_TYPES  = new Set(['PAYMENT_WITHOUT_INVOICE', 'SALARY_NOT_BOOKED', 'OVERDUE_DEBTOR', 'OVERDUE_CREDITOR', 'ADVANCE_NOT_ADJUSTED', 'MISSING_PARTY', 'HIGH_VALUE_EXPENSE', 'STALE_BANK_BALANCE', 'MISSING_HSN_CODE', 'FUTURE_DATED_VOUCHER', 'ABNORMAL_BALANCE', 'ROUND_NUMBER_PAYMENT']);

const LABEL_SHORT = {
  PAYMENT_WITHOUT_INVOICE: 'Payment w/o Invoice', DUPLICATE_VOUCHER_NO: 'Duplicate Voucher',
  ADVANCE_NOT_ADJUSTED: 'Advance Unadjusted',     HIGH_VALUE_EXPENSE: 'High Value Expense',
  STALE_BANK_BALANCE: 'Stale Bank Balance',        OVERDUE_CREDITOR: 'Overdue Creditor',
  OVERDUE_DEBTOR: 'Overdue Debtor',                SALARY_NOT_BOOKED: 'Salary Not Booked',
  STATUTORY_UNPAID: 'Statutory Unpaid',            MISSING_NARRATION: 'Missing Narration',
  MISSING_PARTY: 'Missing Party',                  MISSING_GST: 'Missing GST',
  MISSING_HSN_CODE: 'Missing HSN Code',            UNBALANCED_VOUCHER: 'Unbalanced Voucher',
  EXCESS_PAYMENT: 'Excess Payment',                INCORRECT_GST_RATE: 'Incorrect GST Rate',
  INVALID_GSTIN: 'Invalid GSTIN',                  TDS_NOT_DEDUCTED: 'TDS Not Deducted',
  GST_CALCULATION_ERROR: 'GST Calc Error',         FUTURE_DATED_VOUCHER: 'Future Dated',
  MISSING_GSTIN_PURCHASE: 'Missing GSTIN (Pur)',   ABNORMAL_BALANCE: 'Abnormal Balance',
  ROUND_NUMBER_PAYMENT: 'Round No. Payment',       TRIAL_BALANCE_DIFFERENCE: 'Trial Balance Difference',
};

const LABEL_FULL = {
  STATUTORY_UNPAID: 'Statutory Dues Unpaid',       EXCESS_PAYMENT: 'Excess Payments',
  UNBALANCED_VOUCHER: 'Unbalanced Vouchers',        DUPLICATE_VOUCHER_NO: 'Duplicate Voucher Numbers',
  INCORRECT_GST_RATE: 'Incorrect GST Rate',         PAYMENT_WITHOUT_INVOICE: 'Payments Without Invoice',
  SALARY_NOT_BOOKED: 'Salary Not Booked',           OVERDUE_DEBTOR: 'Overdue Debtors',
  OVERDUE_CREDITOR: 'Overdue Creditors',            ADVANCE_NOT_ADJUSTED: 'Advances Not Adjusted',
  MISSING_GST: 'Missing GST on Sales',              MISSING_PARTY: 'Missing Party on Voucher',
  HIGH_VALUE_EXPENSE: 'High Value Expenses',        STALE_BANK_BALANCE: 'Stale Bank Balance',
  MISSING_HSN_CODE: 'Missing HSN Codes',            INVALID_GSTIN: 'Invalid GSTIN Format',
  TDS_NOT_DEDUCTED: 'TDS Not Deducted',             FUTURE_DATED_VOUCHER: 'Future Dated Vouchers',
  GST_CALCULATION_ERROR: 'GST Calculation Error',   MISSING_NARRATION: 'Missing Narrations',
  MISSING_GSTIN_PURCHASE: 'Missing GSTIN on Purchase', ABNORMAL_BALANCE: 'Abnormal Ledger Balance',
  ROUND_NUMBER_PAYMENT: 'Round Number Payment',     TRIAL_BALANCE_DIFFERENCE: 'Trial Balance Difference',
};

const CRITICAL_PALETTE = ['#f87171', '#ef4444', '#dc2626', '#b91c1c', '#fca5a5', '#fee2e2', '#f97316', '#fb923c', '#fdba74', '#fed7aa'];
const WARNING_PALETTE  = ['#fbbf24', '#f59e0b', '#d97706', '#b45309', '#fcd34d', '#fde68a', '#fef3c7', '#a78bfa', '#8b5cf6', '#7c3aed'];
const INFO_PALETTE     = ['#4f8ef7', '#3b82f6', '#2563eb', '#1d4ed8', '#60a5fa', '#93c5fd', '#bfdbfe', '#6ee7b7', '#34d399', '#10b981'];

// ── Pie chart helpers ─────────────────────────────────────────────────────────
function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  const total = payload[0].payload.total;
  const pct = total ? ((value / total) * 100).toFixed(1) : 0;
  return (
    <div className="ar-pie-tooltip">
      <div className="ar-pie-tooltip-name">{name}</div>
      <div className="ar-pie-tooltip-value">{value} issue{value !== 1 ? 's' : ''} · {pct}%</div>
    </div>
  );
}

function PieLegend({ payload }) {
  return (
    <div className="ar-pie-legend">
      {payload.map((entry, i) => (
        <div key={i} className="ar-pie-legend-item">
          <div className="ar-pie-dot" style={{ background: entry.color }} />
          <span>{entry.value}</span>
          <span className="ar-pie-dot-count">({entry.payload.value})</span>
        </div>
      ))}
    </div>
  );
}

function IssuePieChart({ title, data, palette, accentColor, emptyMsg }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const enriched = data.map(d => ({ ...d, total }));
  return (
    <Card>
      <CardHeader
        title={title}
        right={total > 0 ? (
          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: 'var(--bg3)', color: accentColor, fontWeight: 600 }}>
            {total} issue{total !== 1 ? 's' : ''}
          </span>
        ) : null}
      />
      {total === 0 ? (
        <div className="ar-pie-empty">
          <div className="ar-pie-empty-icon">✓</div>
          {emptyMsg || 'No issues'}
        </div>
      ) : (
        <div className="ar-pie-chart-wrap">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <Pie
                data={enriched}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={65}
                outerRadius={105}
                paddingAngle={2}
                minAngle={4}
              >
                {enriched.map((_, i) => (
                  <Cell key={i} fill={palette[i % palette.length]} stroke="var(--bg2)" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
                fill={accentColor}
                style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--mono)' }}>
                {total}
              </text>
              <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle"
                fill="var(--text3)"
                style={{ fontSize: 11, fontFamily: 'var(--sans)' }}>
                issues
              </text>
            </PieChart>
          </ResponsiveContainer>
          <div className="ar-pie-chart-legend">
            {enriched.map((entry, i) => (
              <div key={i} className="ar-pie-legend-item">
                <div className="ar-pie-dot" style={{ background: palette[i % palette.length] }} />
                <span>{entry.label}</span>
                <span className="ar-pie-dot-count">({entry.count})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Health score ring ─────────────────────────────────────────────────────────
function HealthScore({ score }) {
  const color = score >= 80 ? '#34d399' : score >= 60 ? '#86efac' : score >= 40 ? '#fbbf24' : score >= 20 ? '#f97316' : '#f87171';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Needs Attention' : score >= 20 ? 'Poor' : 'Critical';
  const r = 54, cx = 64, cy = 64;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="ar-health-wrap">
      <svg width="128" height="128" viewBox="0 0 128 128">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth="10" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 64 64)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
        <text x={cx} y={cy - 6} textAnchor="middle" fill={color}
          style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--mono)' }}>{score}</text>
        <text x={cx} y={cy + 16} textAnchor="middle" fill="var(--text3)"
          style={{ fontSize: 11, fontFamily: 'var(--sans)' }}>/ 100</text>
      </svg>
      <span className="ar-health-label" style={{ color }}>{label}</span>
    </div>
  );
}

// ── Paginated + searchable issue detail drawer ────────────────────────────────
const PAGE_SIZE = 10;

function IssueDetailDrawer({ type, group, cfg, ai, onClose }) {
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(0);

  const allItems = group.items || [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? allItems.filter(item =>
        (item.party  || '').toLowerCase().includes(q) ||
        (item.message|| '').toLowerCase().includes(q) ||
        (item.date   || '').toLowerCase().includes(q)
      )
    : allItems;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSearch = (v) => { setSearch(v); setPage(0); };

  const title = LABEL_FULL[type] || type.replace(/_/g, ' ');

  return (
    <div className="ar-drawer" style={{ border: `1px solid ${cfg.border}` }}>
      {/* Drawer header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', background: cfg.headerBg,
        borderBottom: `1px solid ${cfg.border}`,
      }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: cfg.color, flex: 1 }}>
          {title} — {filtered.length} of {allItems.length} item{allItems.length !== 1 ? 's' : ''}
        </span>
        <button className="ar-drawer-close" onClick={onClose} title="Close">✕</button>
      </div>

      {/* Search bar */}
      <div style={{ padding: '8px 14px', borderBottom: `1px solid ${cfg.border}` }}>
        <input
          type="text"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search by party, message or date…"
          style={{
            width: '100%', padding: '6px 10px', borderRadius: 6,
            border: `1px solid ${cfg.border}`, background: 'var(--bg3)',
            color: 'var(--text)', fontSize: 12, fontFamily: 'var(--sans)',
            outline: 'none', boxSizing: 'border-box',
          }}
          onFocus={e => e.target.style.borderColor = cfg.color}
          onBlur={e  => e.target.style.borderColor = cfg.border}
        />
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <div className="ar-drawer-empty">No results for "{search}"</div>
      ) : (
        pageItems.map((item, i) => (
          <div
            key={i}
            className="ar-drawer-item"
            style={{ borderBottom: `1px solid ${cfg.border}` }}
          >
            <div className="ar-drawer-item-header">
              <span className="ar-drawer-item-num">{page * PAGE_SIZE + i + 1}.</span>
              {item.party && (
                <span className="ar-drawer-item-party">{item.party}</span>
              )}
              {item.amount > 0 && (
                <span className="ar-drawer-item-amount" style={{ color: cfg.color }}>
                  ₹{fmt(item.amount)}
                </span>
              )}
              {item.date && (
                <span className="ar-drawer-item-date">
                  {(() => { try { return new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } })()}
                </span>
              )}
            </div>
            {item.message && (
              <div className="ar-drawer-item-message">{item.message}</div>
            )}
          </div>
        ))
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ar-drawer-pagination" style={{ borderTop: `1px solid ${cfg.border}` }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{
              padding: '4px 12px', borderRadius: 6, border: `1px solid ${cfg.border}`,
              background: 'var(--bg2)', color: page === 0 ? 'var(--text3)' : 'var(--text)',
              fontSize: 12, cursor: page === 0 ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--sans)',
            }}
          >← Prev</button>
          <span className="ar-drawer-page-info">
            Page {page + 1} of {totalPages}
            {q && ` · ${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            style={{
              padding: '4px 12px', borderRadius: 6, border: `1px solid ${cfg.border}`,
              background: 'var(--bg2)', color: page === totalPages - 1 ? 'var(--text3)' : 'var(--text)',
              fontSize: 12, cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--sans)',
            }}
          >Next →</button>
        </div>
      )}

      {/* AI owner + deadline footer */}
      {(ai.owner || ai.deadline) && (
        <div style={{
          display: 'flex', gap: 24, padding: '8px 14px', fontSize: 12,
          background: cfg.headerBg, borderTop: `1px solid ${cfg.border}`,
        }}>
          {ai.owner && (
            <div>
              <span className="ar-drawer-footer-label">Owner:</span>
              <span className="ar-drawer-footer-value">{ai.owner}</span>
            </div>
          )}
          {ai.deadline && (
            <div>
              <span className="ar-drawer-footer-label">Deadline:</span>
              <span className="ar-drawer-footer-deadline">{ai.deadline}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IssuesTable({ urgency, groups, startRef = 1, aiLookup = {} }) {
  const [expanded, setExpanded] = useState({});

  const CFG = {
    critical: { dot: '#f87171', color: '#C0392B', border: 'rgba(248,113,113,0.2)', label: 'Critical Issues',  prefix: 'C', headerBg: 'rgba(248,113,113,0.07)' },
    warning:  { dot: '#fbbf24', color: '#D97706', border: 'rgba(251,191,36,0.2)',  label: 'Warning Issues',   prefix: 'W', headerBg: 'rgba(251,191,36,0.07)'  },
    info:     { dot: '#4f8ef7', color: '#2563eb', border: 'rgba(79,142,247,0.2)',  label: 'Info Items',       prefix: 'I', headerBg: 'rgba(79,142,247,0.07)'  },
  };

  const cfg     = CFG[urgency];
  const entries = Object.entries(groups || {});
  if (entries.length === 0) return null;

  const totalCount = entries.reduce((s, [, g]) => s + (g.count || 0), 0);

  return (
    <div className="ar-issues-group">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 16px',
        background: cfg.headerBg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
      }}>
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
        <span style={{
          marginLeft: 4, fontSize: 11, color: cfg.color,
          background: 'var(--bg)', padding: '1px 8px', borderRadius: 10,
          border: `1px solid ${cfg.border}`,
        }}>
          {entries.length} type{entries.length !== 1 ? 's' : ''} · {totalCount} item{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{
        border: `1px solid ${cfg.border}`, borderTop: 'none',
        borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
        overflow: 'hidden',
      }}>
        {/* Column headers */}
        <div style={{
          display: 'grid', gridTemplateColumns: '40px 1fr 48px 180px',
          background: 'var(--bg3)', borderBottom: `1px solid ${cfg.border}`,
        }}>
          {['#', 'Issue · Details', 'Cnt', 'Financial Impact'].map((h, i) => (
            <div
              key={h}
              className={i === 2 ? 'ar-issues-header-cell ar-issues-header-cell--center' : 'ar-issues-header-cell'}
            >{h}</div>
          ))}
        </div>

        {entries.map(([type, group], idx) => {
          const ref      = `${cfg.prefix}${startRef + idx}`;
          const title    = LABEL_FULL[type] || type.replace(/_/g, ' ');
          const isOpen   = !!expanded[type];
          const allItems = group.items || [];
          const hasItems = allItems.length > 0;
          const ai       = aiLookup[type] || {};

          return (
            <div key={type} style={{ borderBottom: idx < entries.length - 1 ? `1px solid ${cfg.border}` : 'none' }}>
              <div
                style={{
                  display: 'grid', gridTemplateColumns: '40px 1fr 48px 180px',
                  background: isOpen ? cfg.headerBg : (idx % 2 === 0 ? 'transparent' : 'var(--bg2)'),
                  cursor: hasItems ? 'pointer' : 'default',
                  transition: 'background 0.1s',
                }}
                onClick={() => hasItems && setExpanded(e => ({ ...e, [type]: !e[type] }))}
                onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'var(--bg3)'; }}
                onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'var(--bg2)'; }}
              >
                <div className="ar-issues-row-ref" style={{ color: cfg.color }}>{ref}</div>
                <div className="ar-issues-row-main">
                  <div className="ar-issues-row-title">{title}</div>
                  {(ai.details || hasItems) && (
                    <div className="ar-issues-row-details">
                      {ai.details ? (
                        <span>{ai.details}</span>
                      ) : (
                        <>
                          {allItems[0].party && (
                            <span className="ar-issues-row-party">{allItems[0].party}</span>
                          )}
                          <span>{allItems[0].message}</span>
                          {allItems.length > 1 && (
                            <span className="ar-issues-row-more">+{allItems.length - 1} more</span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {hasItems && (
                    <div className="ar-issues-row-toggle" style={{ color: cfg.color }}>
                      {isOpen ? '▲ hide' : `▼ view all ${allItems.length}`}
                    </div>
                  )}
                </div>
                <div className="ar-issues-count-cell">
                  <span
                    className="ar-issues-count-badge"
                    style={{ background: cfg.headerBg, color: cfg.color }}
                  >{group.count}</span>
                </div>
                <div className="ar-issues-impact-cell" style={{ color: cfg.color }}>
                  {ai.financial_impact || (group.items?.[0]?.amount ? `₹${fmt(group.items[0].amount)}` : '—')}
                </div>
              </div>

              {isOpen && hasItems && (
                <IssueDetailDrawer
                  type={type}
                  group={group}
                  cfg={cfg}
                  ai={ai}
                  onClose={() => setExpanded(e => ({ ...e, [type]: false }))}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Recommendations & Actions table ──────────────────────────────────────────
function RecommendationsTable({ urgency, actions, aiLookup = {} }) {
  if (!actions || actions.length === 0) return null;

  const CFG = {
    critical: { color: '#C0392B', border: 'rgba(248,113,113,0.2)', label: 'Critical — Immediate Action Required', headerBg: 'rgba(248,113,113,0.07)' },
    warning:  { color: '#D97706', border: 'rgba(251,191,36,0.2)',  label: 'Warning — Action This Quarter',        headerBg: 'rgba(251,191,36,0.07)'  },
    info:     { color: '#2563eb', border: 'rgba(79,142,247,0.2)',  label: 'Info — Best Practice Improvements',   headerBg: 'rgba(79,142,247,0.07)'  },
  };

  const cfg = CFG[urgency];

  return (
    <div className="ar-rec-group">
      <div style={{
        padding: '8px 16px',
        background: cfg.headerBg, border: `1px solid ${cfg.border}`,
        borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
        fontSize: 11, fontWeight: 700, color: cfg.color,
        textTransform: 'uppercase', letterSpacing: '0.07em',
      }}>
        {cfg.label}
      </div>

      <div style={{
        border: `1px solid ${cfg.border}`, borderTop: 'none',
        borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '40px 1fr 120px',
          background: 'var(--bg3)', borderBottom: `1px solid ${cfg.border}`,
        }}>
          {['Ref', 'Action Required · Owner', 'Deadline'].map(h => (
            <div key={h} className="ar-rec-col-header">{h}</div>
          ))}
        </div>

        {actions.map((action, idx) => {
          const ai  = aiLookup[action.type] || {};
          const ref = ai.ref || action.no || `${idx + 1}`;
          return (
            <div
              key={action.type || idx}
              className={idx % 2 === 0 ? 'ar-rec-row--even' : 'ar-rec-row--odd'}
              style={{
                display: 'grid', gridTemplateColumns: '40px 1fr 120px',
                borderBottom: idx < actions.length - 1 ? `1px solid ${cfg.border}` : 'none',
              }}
            >
              <div className="ar-rec-row-ref" style={{ color: cfg.color }}>{ref}</div>
              <div className="ar-rec-row-main">
                <div className="ar-rec-row-title">
                  {LABEL_FULL[action.type] || action.type?.replace(/_/g, ' ')}
                  {action.count > 0 && (
                    <span
                      className="ar-rec-row-count"
                      style={{ background: cfg.headerBg, color: cfg.color }}
                    >{action.count}</span>
                  )}
                </div>
                <div className="ar-rec-row-action" style={{ marginBottom: ai.owner ? 4 : 0 }}>
                  {action.action}
                </div>
                {ai.owner && (
                  <div className="ar-rec-row-owner">
                    <span className="ar-rec-row-owner-label">Owner: </span>{ai.owner}
                  </div>
                )}
              </div>
              <div className="ar-rec-deadline">{ai.deadline || '—'}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AuditReport({ sourceId, syncRunId, userRole }) {
  const { data, loading }                = useApi(() => api.auditReport(sourceId, syncRunId),        [sourceId, syncRunId]);
  const { data: aiData, loading: aiLoading,
          refetch: refetchAi }           = useApi(() => api.getAiReport(syncRunId),                  [syncRunId]);
  const { data: issueData, loading: il } = useApi(() => api.auditSummary(sourceId, null, syncRunId), [sourceId, syncRunId]);

  const [generating,     setGenerating]     = useState(false);
  const [genError,       setGenError]       = useState(null);
  const [selectedPdfIds, setSelectedPdfIds] = useState(new Set());

  const isMobile = useIsMobile();

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      await api.generateAiReport(syncRunId);
      refetchAi();
    } catch (err) {
      const msg = err.message || 'Generation failed — please try again.';
      setGenError(msg);
      console.error('[AuditReport] AI generation failed:', msg);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <PageWrap><PageHeader title="Audit Report" /><Skeleton rows={8} /></PageWrap>;
  if (!data)   return <PageWrap><PageHeader title="Audit Report" /><div className="ar-no-data">No data — run a sync first.</div></PageWrap>;

  // ── AI lookup: flat map of issue_type → AI item ───────────────────────────
  const aiReport = aiData?.report || {};
  const aiLookup = [
    ...(aiReport.critical_issues || []),
    ...(aiReport.warning_issues  || []),
    ...(aiReport.info_items       || []),
  ].reduce((acc, item) => {
    if (item.issue_type) acc[item.issue_type] = item;
    return acc;
  }, {});

  const aiMissing = !aiLoading && !aiData;

  // ── Pie chart data (deterministic) ────────────────────────────────────────
  const issueSummary = issueData?.issueSummary || {};
  const toChartData  = (filterFn) =>
    Object.entries(issueSummary).filter(([type]) => filterFn(type)).sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ label: LABEL_SHORT[type] || type.replace(/_/g, ' '), count }));
  const criticalData = toChartData(t => CRITICAL_TYPES.has(t));
  const warningData  = toChartData(t => WARNING_TYPES.has(t));
  const infoData     = toChartData(t => !CRITICAL_TYPES.has(t) && !WARNING_TYPES.has(t));

  // ── Groups + actions (deterministic) ─────────────────────────────────────
  const toGroupMap = (summarySection) =>
    (summarySection?.groups || []).reduce((acc, g) => ({ ...acc, [g.type]: g }), {});

  const actionsByUrgency = (data.actions || []).reduce((acc, a) => {
    const u = a.urgency || 'info';
    if (!acc[u]) acc[u] = [];
    acc[u].push(a);
    return acc;
  }, {});

  // ── Stats (deterministic) ─────────────────────────────────────────────────
  const STATS = [
    ['Total Vouchers', data.totalVouchers?.toLocaleString('en-IN'), 'var(--text)'],
    ['Total Issues',   data.totalIssues?.toLocaleString('en-IN'),   '#f87171'],
    ['Open Issues',    data.openIssues?.toLocaleString('en-IN'),    '#fbbf24'],
    ['Resolved',       data.resolvedIssues?.toLocaleString('en-IN'),'#34d399'],
    ['Critical',       data.summary?.critical?.count ?? 0,          '#f87171'],
    ['Warnings',       data.summary?.warning?.count  ?? 0,          '#fbbf24'],
  ];

  const selectedIdsParam = [...selectedPdfIds].join(',');
  const htmlReportUrl = selectedPdfIds.size > 0
    ? `${BASE}/ai-report/${syncRunId}/html?commentIds=${selectedIdsParam}`
    : `${BASE}/ai-report/${syncRunId}/html`;

  const SectionDivider = ({ title, sub }) => (
    <div className="ar-section-divider">
      <span className="ar-section-divider-title">{title}</span>
      <div className="ar-section-divider-line" />
      {sub && <span className="ar-section-divider-sub">{sub}</span>}
    </div>
  );

  return (
    <PageWrap>
      <PageHeader
        title="Audit Report"
        sub={`${data.company} · Period: ${data.period} · Synced: ${fmtDate(data.syncedAt)}`}
      />

      {/* ── AI report banner + Download button ── */}
      <div className="ar-toolbar">
        {aiMissing && (
          <div className="ar-ai-banner">
            <span className="ar-ai-banner-text">
              ⚠ AI report not yet generated — Financial Impact, Owner and Deadline will be missing until generated.
            </span>
            <div className="ar-ai-banner-actions">
              {genError && <span className="ar-ai-gen-error">{genError}</span>}
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="ar-btn-generate"
              >
                {generating ? 'Generating…' : 'Generate AI Report'}
              </button>
            </div>
          </div>
        )}

        {!aiMissing && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <a
              href={htmlReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ar-btn-download"
              title="Opens report in a new tab — click Save as PDF to download"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              View / Print Report
            </a>
            <a
              href={selectedPdfIds.size > 0
                ? `${BASE}/ai-report/${syncRunId}/excel?commentIds=${selectedIdsParam}`
                : `${BASE}/ai-report/${syncRunId}/excel`}
              download
              className="ar-btn-excel"
              title="Download audit report as Excel workbook"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="11" x2="12" y2="17" />
                <polyline points="9 14 12 17 15 14" />
              </svg>
              Excel
            </a>
          </div>
        )}
      </div>

      {/* ── Health score + stats ── */}
      <div className="ar-health-grid" style={{ gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr' }}>
        <Card style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HealthScore score={data.healthScore} />
        </Card>
        <Card>
          <div style={{
            padding: '16px 20px', display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)',
            gap: 0,
          }}>
            {STATS.map(([label, value, color]) => (
              <div key={label} style={{
                padding: isMobile ? '10px 12px' : '12px 16px',
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
              }}>
                <div className="ar-stat-label">{label}</div>
                <div className="ar-stat-value" style={{ fontSize: isMobile ? 20 : 24, color }}>{value ?? '—'}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Pie charts ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
        {il ? (
          [0, 1, 2].map(i => <Card key={i}><Skeleton rows={5} /></Card>)
        ) : (
          <>
            <IssuePieChart title="🔴 Critical" data={criticalData} palette={CRITICAL_PALETTE} accentColor="#f87171" emptyMsg="No critical issues" />
            <IssuePieChart title="🟡 Warnings" data={warningData}  palette={WARNING_PALETTE}  accentColor="#fbbf24" emptyMsg="No warnings" />
            <IssuePieChart title="🔵 Info"     data={infoData}     palette={INFO_PALETTE}     accentColor="#4f8ef7" emptyMsg="No info issues" />
          </>
        )}
      </div>

      {/* ── Issues Identified ── */}
      <SectionDivider title="Issues Identified" sub="Findings from latest sync" />
      <IssuesTable urgency="critical" groups={toGroupMap(data.summary?.critical)} startRef={1} aiLookup={aiLookup} />
      <IssuesTable urgency="warning"  groups={toGroupMap(data.summary?.warning)}  startRef={1} aiLookup={aiLookup} />
      <IssuesTable urgency="info"     groups={toGroupMap(data.summary?.info)}     startRef={1} aiLookup={aiLookup} />

      {/* ── Recommendations & Action Items ── */}
      <SectionDivider title="Recommendations & Action Items" sub="Prioritised actions" />
      <RecommendationsTable urgency="critical" actions={actionsByUrgency.critical} aiLookup={aiLookup} />
      <RecommendationsTable urgency="warning"  actions={actionsByUrgency.warning}  aiLookup={aiLookup} />
      <RecommendationsTable urgency="info"     actions={actionsByUrgency.info}     aiLookup={aiLookup} />

      {/* ── Disclaimer ── */}
      <div className="ar-disclaimer">
        Advisory report only. Financial impact figures are computed from raw Tally data and are indicative.
        Not a substitute for a formal audit opinion.
      </div>

      {/* ── Comments ── */}
      <div className="ar-comments-section">
        <div className="ar-section-divider">
          <span className="ar-section-divider-title">Comments & Responses</span>
          <div className="ar-section-divider-line" />
          <span className="ar-section-divider-sub">Auditor observations · Customer replies</span>
        </div>
        <CommentsSection
          sourceId={sourceId}
          syncRunId={syncRunId}
          userRole={userRole}
          selectedPdfIds={selectedPdfIds}
          setSelectedPdfIds={setSelectedPdfIds}
        />
      </div>
    </PageWrap>
  );
}
